interface RoundTripField {
  id: string;
  name: string;
  description: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  required: boolean;
  parentId?: string;
  coverageTypeId?: number;
  coverageTypeName?: string;
  sublimitTypeId?: number;
  sublimitTypeName?: string;
  subretentionTypeId?: number;
  subretentionTypeName?: string;
}

const ITEM_TYPE_KEYS = [
  'coverageTypeId',
  'coverageTypeName',
  'sublimitTypeId',
  'sublimitTypeName',
  'subretentionTypeId',
  'subretentionTypeName',
] as const;

const isTypedItem = (field: RoundTripField): boolean => !!(field.coverageTypeId || field.sublimitTypeId || field.subretentionTypeId);

const copyItemTypeKeys = (target: Record<string, any>, source: Record<string, any>) => {
  for (const key of ITEM_TYPE_KEYS) {
    if (source[key] != null && source[key] !== '') target[key] = source[key];
  }
};

const defaultItemDescription = (itemDef: any): string => {
  if (itemDef.coverageTypeName) return `Coverage line: ${itemDef.coverageTypeName}`;
  if (itemDef.sublimitTypeName) return `Sublimit: ${itemDef.sublimitTypeName}`;
  if (itemDef.subretentionTypeName) return `Subretention: ${itemDef.subretentionTypeName}`;
  return 'Array item object structure';
};

let idCounter = 0;
const generateId = () => `rt_${++idCounter}`;

const getRootFields = (fields: RoundTripField[]): RoundTripField[] => fields.filter((f) => !f.parentId);

const getChildrenOf = (parentId: string, fields: RoundTripField[]): RoundTripField[] => fields.filter((f) => f.parentId === parentId);

// The API reads an array descriptor's properties[0] as the item template and ignores
// the rest (bedrock.go descriptorToSchema). An array of objects therefore has to carry
// a single object item node; flattening its fields into the array loses everything
// after the first one.
const itemNodeNameFor = (arrayName: string): string => {
  if (arrayName === 'coverageLines') return '_coverageLineItem';
  if (arrayName === 'sublimits') return '_sublimitItem';
  if (arrayName === 'subretentions') return '_subretentionItem';
  return '_item';
};

const isItemNode = (field: { name: string; type: string }): boolean => field.name.startsWith('_') && field.type === 'object';

// Incoming JSON is hand-editable, so an item node may arrive without a declared type.
// Treating it as a leaf would flatten the array and discard the item's fields.
const isItemDescriptor = (descriptor: any): boolean =>
  !!descriptor && typeof descriptor.name === 'string' && descriptor.name.startsWith('_') && (descriptor.type === 'object' || descriptor.type == null);

const explicitItemDescriptors = (properties: any[]): any[] => properties.filter(isItemDescriptor);

// Builds the item node(s) beneath an array field. Descriptors that already carry an
// explicit item node are taken as-is; a legacy flattened array gets one synthesized so
// it re-serializes in the shape the API can actually read.
function parseArrayItems(arrayName: string, properties: any[], arrayFieldId: string, fields: RoundTripField[]): void {
  const explicit = explicitItemDescriptors(properties);

  if (explicit.length > 0) {
    for (const itemDef of explicit) {
      const itemObject: RoundTripField = {
        id: generateId(),
        name: itemDef.name,
        description: itemDef.description || defaultItemDescription(itemDef),
        type: 'object',
        required: itemDef.required === true,
        parentId: arrayFieldId,
      };
      copyItemTypeKeys(itemObject, itemDef);
      fields.push(itemObject);

      if (itemDef.properties?.length) {
        parseChildFields(itemDef.properties, itemObject.id, fields);
      }
    }
    return;
  }

  const placeholder: RoundTripField = {
    id: generateId(),
    name: itemNodeNameFor(arrayName),
    description: 'Array item object structure',
    type: 'object',
    required: false,
    parentId: arrayFieldId,
  };
  fields.push(placeholder);
  parseChildFields(properties, placeholder.id, fields);
}

function parseChildFields(properties: any[], parentId: string, fields: RoundTripField[]): void {
  if (!properties?.length) return;

  for (const childProp of properties) {
    if (!childProp.name) continue;

    const child: RoundTripField = {
      id: generateId(),
      name: childProp.name,
      description: childProp.description || '',
      type: childProp.type || 'string',
      required: childProp.required === true,
      parentId,
    };
    fields.push(child);

    if (childProp.properties?.length) {
      if (childProp.type === 'object') {
        parseChildFields(childProp.properties, child.id, fields);
      } else if (childProp.type === 'array') {
        parseArrayItems(child.name, childProp.properties, child.id, fields);
      }
    }
  }
}

function parseSchema(schema: any[]): RoundTripField[] {
  const fields: RoundTripField[] = [];

  for (const fieldDef of schema) {
    if (!fieldDef.name) continue;

    const field: RoundTripField = {
      id: generateId(),
      name: fieldDef.name,
      description: fieldDef.description || '',
      type: fieldDef.type || 'string',
      required: fieldDef.required === true,
    };
    fields.push(field);

    if (fieldDef.properties?.length) {
      if (fieldDef.type === 'object') {
        parseChildFields(fieldDef.properties, field.id, fields);
      } else if (fieldDef.type === 'array') {
        parseArrayItems(field.name, fieldDef.properties, field.id, fields);
      }
    }
  }

  return fields;
}

function buildItemDef(item: RoundTripField, allFields: RoundTripField[]): Record<string, any> {
  const itemDef: Record<string, any> = {
    name: item.name,
    type: 'object',
    description: item.description,
    required: item.required,
  };
  copyItemTypeKeys(itemDef, item);

  const itemChildren = getChildrenOf(item.id, allFields).filter((c) => !c.name.startsWith('_'));
  if (itemChildren.length > 0) {
    itemDef.properties = itemChildren.map((c) => buildFieldDef(c, allFields));
  }
  return itemDef;
}

function buildFieldDef(field: RoundTripField, allFields: RoundTripField[]): Record<string, any> {
  const def: Record<string, any> = {
    name: field.name,
    type: field.type,
    description: field.description,
    required: field.required,
  };

  const children = getChildrenOf(field.id, allFields);

  if (field.type === 'object' && children.length > 0) {
    def.properties = [];
    for (const child of children) {
      if (child.name.startsWith('_')) {
        const placeholderChildren = getChildrenOf(child.id, allFields);
        for (const gc of placeholderChildren) {
          if (!gc.name.startsWith('_')) {
            def.properties.push(buildFieldDef(gc, allFields));
          }
        }
      } else {
        def.properties.push(buildFieldDef(child, allFields));
      }
    }
  }

  if (field.type === 'array' && children.length > 0) {
    // Typed items take precedence: the API's union gate requires every child to carry a
    // type name, so an untyped sibling would collapse the whole array back to its first
    // element.
    const typedItems = children.filter(isTypedItem);
    const itemNodes = typedItems.length > 0 ? typedItems : children.filter((c) => isItemNode(c));

    if (itemNodes.length > 0) {
      def.properties = itemNodes.map((item) => buildItemDef(item, allFields));
    } else {
      def.properties = children.filter((c) => !c.name.startsWith('_')).map((c) => buildFieldDef(c, allFields));
    }
  }

  return def;
}

function generateSchema(fields: RoundTripField[]): string {
  if (fields.length === 0) return '';

  const rootFields = getRootFields(fields);
  const schema = rootFields.map((f) => buildFieldDef(f, fields));
  return JSON.stringify(schema, null, 2);
}

/**
 * Round-trip a schema string through parse → generate to normalize its format.
 * This ensures published (old format) and current (new format) schemas produce
 * identical output for unchanged content, so diffs only show real changes.
 * Returns null on parse failure.
 */
export const roundTripSchema = (raw: string): string | null => {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    idCounter = 0;
    const fields = parseSchema(parsed);
    return generateSchema(fields);
  } catch {
    return null;
  }
};
